import type { IAllProps } from '@tinymce/tinymce-react'
import { Editor } from '@tinymce/tinymce-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { withFormikSupport } from '../../hocs/withFormikSupport'
import { InputUI } from '../../ui'
import { VALID_ELEMENTS, VALID_STYLES } from './constants'
import * as S from './styled'
import type { TinyMCEButtons, TinyMCEProps } from './types'
import { getToolbar, mapValidElementsToString, mapValidStyles, setupInserter } from './utils'

const toolbarDefaults: TinyMCEButtons = {
  alignment: false,
  blocks: false,
  code: false,
  fullscreen: false,
  table: false,
  image: false,
  customImage: false,
}

export const TinyMCE = withFormikSupport(
  ({
    tinymceScriptSrc,
    contentStyles,
    placeholder,
    resize = false,
    statusbar = false,
    buttons,
    inserterOptions,
    height = 160,
    name = '',
    initialValue,
    value,
    disabled = false,
    invalid = false,
    error,
    submitCount,
    invalidElements,
    imagesUploadHandler,
    appearance = 'light',
    onChange,
    onValueChange,
    onSetup,
    onBlur,
  }: TinyMCEProps) => {
    const touchedRef = useRef(false)
    const [focused, setFocused] = useState(false)

    const getContentStyle = useCallback(
      (colorScheme: string) => {
        return `
          ${contentStyles}

          html {
            min-height: 100%;
            cursor: text;
          }

          .mce-content-body {
            color-scheme: ${colorScheme};
            margin: var(--space-400);
            font-family: var(--font-family-body);
            font-size: var(--font-size-400);
            color: var(--text-primary);
            background-color: var(--bg-primary);
          }

          .mce-content-body,
          .mce-content-body > *:first-child {
            margin-top: var(--space-200);
          }

          .mce-content-body a {
            color: var(--text-brand);
          }

          .mce-content-body [data-mce-selected=inline-boundary] {
            background: var(--bg-brand-secondary);
          }

          .mce-content-body img {
            display: block;
            margin: 1em auto;
            max-width: 100%;
            height: auto;
          }

          .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before {
            color: var(--text-secondary);
          }
        `
      },
      [contentStyles],
    )

    useEffect(() => {
      // NOTE: reset touched state after each form reset to prevent initial value encoding
      if (!focused && value === initialValue) {
        touchedRef.current = false
      }
    }, [value, initialValue, focused])

    const init: IAllProps['init'] = useMemo(
      () => ({
        plugins: 'lists link table code fullscreen autoresize autolink',
        menubar: false,
        toolbar: getToolbar({ ...toolbarDefaults, ...buttons }),
        toolbar_mode: 'wrap',
        contextmenu: false,
        elementpath: false,
        max_height: 800,
        min_height: 160,
        branding: false,
        resize,
        statusbar: statusbar || resize,
        setup: (editor) => {
          setupInserter(editor, inserterOptions)
          // FIX for https://popshop.atlassian.net/browse/PRD-2381
          // solution: https://stackoverflow.com/a/67166716/7518346
          editor.on('paste cut', () => {
            editor.setDirty(true) // this updates the editor.undoManager
          })

          onSetup?.(editor)
        },
        placeholder,
        width: '100%',
        height,
        content_style: getContentStyle(appearance),
        valid_styles: mapValidStyles(VALID_STYLES),
        valid_elements: mapValidElementsToString(VALID_ELEMENTS),
        relative_urls: false,
        invalid_elements: invalidElements,
        images_upload_handler: imagesUploadHandler,
        image_dimensions: false,
        paste_data_images: false,
        object_resizing: false,
        remove_script_host: false,
        link_assume_external_targets: 'https',
      }),
      [buttons, height, placeholder, inserterOptions, onSetup, resize, statusbar, appearance],
    )

    const handleEditorChange: NonNullable<IAllProps['onEditorChange']> = useCallback(
      (newValue) => {
        // NOTE: handleEditorChange is called before useEffect which sets touchedRef.current to false so we need to
        // prevent encoding entities by TinyMCE after form is being reset
        if (!focused && value === initialValue) return

        if (!touchedRef.current) return

        if (onChange && name) onChange({ target: { name, value: newValue } })
        if (onValueChange) onValueChange(newValue)
      },
      [focused, name, value, initialValue, onChange, onValueChange],
    )

    const handleFocus: NonNullable<IAllProps['onFocus']> = useCallback(() => {
      if (!touchedRef.current) touchedRef.current = true

      setFocused(true)
    }, [])

    const handleBlur: NonNullable<IAllProps['onBlur']> = useCallback(
      (event, editor) => {
        setFocused(false)

        const value = editor.getContent({ format: 'html' })

        if (onBlur) onBlur({ target: { name, value } })
      },
      [onBlur, name],
    )

    const renderDarkModeGlobalStyle = () => {
      if (
        appearance === 'light dark' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
      ) {
        return <S.DarkModeGlobalStyle />
      }

      if (appearance === 'dark') {
        return <S.DarkModeGlobalStyle />
      }

      return null
    }

    return (
      <S.Container data-testid="TinyMCE">
        {renderDarkModeGlobalStyle()}
        <InputUI.Box $fullHeight $disabled={disabled} $focused={focused} $invalid={invalid}>
          <Editor
            tinymceScriptSrc={tinymceScriptSrc}
            init={init}
            initialValue={initialValue}
            value={value}
            disabled={disabled}
            onEditorChange={handleEditorChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </InputUI.Box>
        {!!error && <InputUI.Error errorTimestampKey={submitCount}>{error}</InputUI.Error>}
      </S.Container>
    )
  },
)
